/**
 * GMA-1620 (CompassCare Web 5/14 — Clients) — client detail drawer body.
 *
 * The sub-tabbed body inside the Clients DetailDrawer: Overview / Environment /
 * Devices / History / Notes. Only the active tab's content is mounted (the AC's
 * "sub-tabs lazy-load"). This slice ships the tab shell + the Overview content
 * (status, living environment, home, last contact, emergency contacts + care
 * team); the Environment access-instructions permission gate and the richer
 * Devices/History/Notes bodies are follow-on slices, so the other tabs render a
 * graceful "no data yet" state until then.
 *
 * Presentational + fail-safe: a null client renders nothing. a11y: an ARIA
 * tablist with selected-state tabs + a tabpanel; colors from the design tokens.
 */
import React, { useState, useEffect, useId } from "react";
import PropTypes from "prop-types";
import SeverityBadge from "./SeverityBadge";
import { theme } from "../../styles/designTokens";

export const CLIENT_DETAIL_TABS = Object.freeze([
  { key: "overview", label: "Overview" },
  { key: "environment", label: "Environment" },
  { key: "devices", label: "Devices" },
  { key: "history", label: "History" },
  { key: "notes", label: "Notes" },
]);

const ENV_LABELS = Object.freeze({
  group_home: "Group home",
  independent: "Independent living",
});

const label = { minWidth: 120, color: theme.textMuted, fontSize: 13 };

function Row({ label: l, children }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
      <dt style={label}>{l}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  );
}

function OverviewTab({ client }) {
  const contacts = Array.isArray(client.contacts) ? client.contacts.filter(Boolean) : [];
  return (
    <div data-testid="client-tab-overview" style={{ display: "grid", gap: 12 }}>
      <dl style={{ margin: 0, display: "grid", gap: 8 }}>
        <Row label="Status">
          {client.online === false ? (
            <span style={{ color: theme.textMuted }}>Offline</span>
          ) : (
            <SeverityBadge level={client.status} />
          )}
        </Row>
        <Row label="Living env">{ENV_LABELS[client.livingEnv] || "—"}</Row>
        <Row label="Home">{client.home || "—"}</Row>
        <Row label="Last contact">{client.lastContact || "—"}</Row>
      </dl>

      <section aria-label="Emergency contacts">
        <div style={{ ...label, marginBottom: 6 }}>Emergency contacts &amp; care team</div>
        {contacts.length ? (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {contacts.map((c, i) => (
              <li key={c.phone || `${c.name}-${i}`} style={{ fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{c.name || "Contact"}</span>
                {c.role && <span style={{ color: theme.textMuted }}> · {c.role}</span>}
                {c.phone && <span style={{ color: theme.textMuted }}> · {c.phone}</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ margin: 0, color: theme.textMuted, fontSize: 13 }}>No contacts on file</p>
        )}
      </section>
    </div>
  );
}

OverviewTab.propTypes = { client: PropTypes.object.isRequired };

// Placeholder body for the tabs whose full content is a later slice.
function PendingTab({ tabKey, tabLabel }) {
  return (
    <div
      data-testid={`client-tab-${tabKey}`}
      style={{ color: theme.textMuted, fontSize: 13, padding: "8px 0" }}
    >
      {tabLabel} details are coming soon.
    </div>
  );
}

PendingTab.propTypes = { tabKey: PropTypes.string, tabLabel: PropTypes.string };

export default function ClientDetailBody({ client }) {
  const [active, setActive] = useState("overview");
  // Per-instance ARIA ids so two ClientDetailBody instances on one page (side-by-side
  // drawers, comparisons) don't collide their tab/panel relationships or steal focus.
  const baseId = useId();
  const panelId = `${baseId}-tabpanel`;
  const tabButtonId = (key) => `${baseId}-tab-${key}`;
  // Reset to Overview when a different client is shown (the drawer can stay open while
  // the selection changes) — otherwise the new client would default to the prior tab.
  const clientId = client?.id ?? null;
  useEffect(() => {
    setActive("overview");
  }, [clientId]);
  if (!client) return null;
  const activeTab = CLIENT_DETAIL_TABS.find((t) => t.key === active) || CLIENT_DETAIL_TABS[0];

  // ARIA tabs keyboard model: Arrow keys move (and activate) between tabs, Home/End jump
  // to the first/last. All tab buttons are always mounted, so focusing by id is synchronous.
  const onTabKeyDown = (e) => {
    const keys = CLIENT_DETAIL_TABS.map((t) => t.key);
    const idx = keys.indexOf(active);
    let nextIdx = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") nextIdx = (idx + 1) % keys.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") nextIdx = (idx - 1 + keys.length) % keys.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = keys.length - 1;
    if (nextIdx === null) return;
    e.preventDefault();
    const nextKey = keys[nextIdx];
    setActive(nextKey);
    document.getElementById(tabButtonId(nextKey))?.focus();
  };

  return (
    <div style={{ fontFamily: theme.fontUI, color: theme.textPrimary, display: "grid", gap: 12 }}>
      <div
        role="tablist"
        aria-label="Client detail sections"
        style={{ display: "flex", flexWrap: "wrap", gap: 4, borderBottom: `1px solid ${theme.border}`, paddingBottom: 8 }}
      >
        {CLIENT_DETAIL_TABS.map((t) => {
          const selected = t.key === active;
          return (
            <button
              key={t.key}
              id={tabButtonId(t.key)}
              type="button"
              role="tab"
              aria-selected={selected}
              aria-controls={panelId}
              tabIndex={selected ? 0 : -1}
              data-testid={`client-detail-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              onKeyDown={onTabKeyDown}
              style={{
                padding: "5px 10px",
                borderRadius: 7,
                border: "none",
                background: selected ? theme.surface : "transparent",
                color: selected ? theme.textPrimary : theme.textMuted,
                cursor: "pointer",
                fontFamily: theme.fontUI,
                fontSize: 12.5,
                fontWeight: 700,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={tabButtonId(active)}
        tabIndex={0}
      >
        {active === "overview" ? (
          <OverviewTab client={client} />
        ) : (
          <PendingTab tabKey={activeTab.key} tabLabel={activeTab.label} />
        )}
      </div>
    </div>
  );
}

ClientDetailBody.propTypes = {
  // Selected client; null renders nothing. Optional `contacts` [{name,role,phone}].
  client: PropTypes.object,
};
